import React, { useEffect, useState } from "react";
import "../styles/editDailyScheduleModal.css";
import { ZensoryModule } from "../ZensoryModule";
import { toLocalISOString, formatTimeInput } from "./Util/TimeUtil";
import { LOGGER, LogLevel } from "./Util/Logger";
import SpinnerLoader from "./SpinnerLoader";

const FIELD_NAME = {
  startOfDay: "Start of day",
  postLunch: "Post lunch reset",
  windDown: "Wind down",
};

const EditDailyScheduleModal = ({ onClose, initialData, onSubmit }) => {
  const [scheduleData, setScheduleData] = useState(
    initialData || {
      startOfDay: {
        id: "",
        time: "",
      },
      postLunch: {
        id: "",
        time: "",
      },
      windDown: {
        id: "",
        time: "",
      },
    }
  );
  useEffect(() => {
    if (initialData) {
      setScheduleData(initialData);
    }
  }, [initialData]);
  
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [loading, setLoading] = useState(false);

  const validateField = (field, value) => {
    if (!value.trim()) {
      return `${FIELD_NAME[field]} time cannot be empty.`;
    }
    
    const timeRegex = /^(?:[01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(value)) {
      return "Enter a valid time in HH:MM format (e.g., 09:30).";
    }
    
    return null; // No error
  };

  const handleTimeChange = (field, e) => {
    const input = e.target.value;
    const formattedInput = formatTimeInput(input);

    if (formattedInput.length > 5) {
      return;
    }
    let timeValue = formattedInput;
    setScheduleData((prevData) => ({
      ...prevData,
      [field]: {
        ...prevData[field],
        time: timeValue,
      },
    }));

    // Validate the specific field
    const newErrors = { ...errors };
    if (!formattedInput.trim()) {
      newErrors[field] = `${FIELD_NAME[field]} time cannot be empty.`;
    } else {
      const timeRegex = /^(?:[01]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(formattedInput)) {
        newErrors[field] = "Enter a valid time in HH:MM format (e.g., 09:30).";
      } else {
        delete newErrors[field];
      }
    }

    setErrors(newErrors);

    // Update global error if needed
   
  };

  const handleTimeBlur = (field, e) => {
    const timeValue = e.target.value;
    const formattedInput = formatTimeInput(timeValue);

    // Update the field value if it was reformatted
    if (formattedInput !== timeValue) {
      setScheduleData((prevData) => ({ 
        ...prevData, 
        [field]: { ...prevData[field], time: formattedInput } 
      }));
    }

    const fieldError = validateField(field, formattedInput);
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (fieldError) {
        newErrors[field] = fieldError;
      } else {
        delete newErrors[field];
      }
      return newErrors;
    });
  };

  const validateForm = () => {
    const fields = ['startOfDay', 'postLunch', 'windDown'];
    const newErrors = {};
    
    fields.forEach(field => {
      const error = validateField(field, scheduleData[field].time);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

   // Helper function to parse time string into hours and minutes
  const parseTimeToHoursMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    return { hours, minutes };
  };

  // Helper function to create event object
  const createEvent = (timeString, eventId, timeLabel) => {
    try {
      const { hours, minutes } = parseTimeToHoursMinutes(timeString);
      const todayDate = new Date();
      const eventDateTime = new Date(todayDate);
      eventDateTime.setHours(hours, minutes, 0, 0);
      const startTime = toLocalISOString(eventDateTime);
      
      return {
        eventId: eventId,
        updateData: {
          start: {
            dateTime: startTime,
          },
        },
      };
    } catch (parseError) {
      LOGGER.log(LogLevel.DEBUG, `Error parsing ${timeLabel} time: ${JSON.stringify(parseError)}`);
      throw new Error(`Invalid time format for ${timeLabel}. Please use HH:MM format.`);
    }
  };

  const handleSubmit = async (e) => {
     e.preventDefault();
     if (!validateForm()) {
       return;
     }
 
     setLoading(true);
 
     try {
       const userId = ZensoryModule.getAuth().getCurrentUser();
       if (!userId) {
         LOGGER.log(LogLevel.DEBUG, `No authenticated user found. userId: ${JSON.stringify(userId)}`);
         setLoading(false);
         setGlobalError("You must be logged in to update your schedule.");
         return;
       }
       
       const events = [];
 
       if (scheduleData.startOfDay.time) {
         try {
           events.push(createEvent(
             scheduleData.startOfDay.time,
             scheduleData.startOfDay.id,
             "Start of day"
           ));
         } catch (parseError) {
           setGlobalError(parseError.message);
           setLoading(false);
           return;
         }
       }
 
       if (scheduleData.postLunch.time) {
         try {
           events.push(createEvent(
             scheduleData.postLunch.time,
             scheduleData.postLunch.id,
             "Post Lunch Reset"
           ));
         } catch (parseError) {
           setGlobalError(parseError.message);
           setLoading(false);
           return;
         }
       }
 
       if (scheduleData.windDown.time) {
         try {
           events.push(createEvent(
             scheduleData.windDown.time,
             scheduleData.windDown.id,
             "Wind Down"
           ));
         } catch (parseError) {
           setGlobalError(parseError.message);
           setLoading(false);
           return;
         }
       }
 
       if (events.length > 0) {
         await ZensoryModule.getCalenderApi().updateCalenderschedule({
           userId: userId,
           events: events,
         });
       }
       if (onSubmit) {
         onSubmit(scheduleData);
       }
       onClose();
     } catch (error) {
       LOGGER.log(LogLevel.DEBUG, `Error updating daily schedule: ${JSON.stringify(error)}`);
       setGlobalError("Failed to update daily schedule. Please try again. Error: " + error.message);
     } finally {
       setLoading(false);
     }
   };

  const handleOnClose = () => {
    onClose();
  };

  return (
    <div className="sche-modal-overlay" id="editDailyScheduleModal" onClick={handleOnClose}>
      <div
        className="modal-content-wrapper custom-gradient-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-close">
          <div className="close-button" onClick={handleOnClose}>
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
        <div className="sche-modal-content-wrapper">
        <div className="sche-modal-content">
          <div className="modal-header">
            <h2 className="modal-title">Set Default Daily Schedule Times</h2>
              <p className="modal-description">
                Choose the three times in your day when you'd like The Zensory
                to guide you into mindfulness. These sessions will appear in
                your calendar as gentle reminders to reset, refocus, and
                recharge.
              </p>
          </div>
          <form onSubmit={handleSubmit} className="dailySche-form">
            <div className="modal-body">
              {globalError && <div className="error-message">{globalError}</div>}
              <div className="schedule-time-input">
                <div className="form-group">
                  <label htmlFor="startOfDay">START THE DAY</label>
                  <p className="time-description">
                    What time do you usually start your work day?
                  </p>
                  <div className="input-wrapper custom-input-gradient-border">
                    <input
                      type="text"
                      id="startOfDay"
                      className="input-field"
                      placeholder="Enter time"
                      value={scheduleData?.startOfDay?.time}
                      onChange={(e) => handleTimeChange("startOfDay", e)}
                      onBlur={(e) => handleTimeBlur("startOfDay", e)}
                      aria-invalid={!!errors.startOfDay}
                    />
                  </div>
                  {errors.startOfDay && (
                    <div className="error-message">{errors.startOfDay}</div>
                  )}
                </div>
              </div>

              <div className="schedule-time-input">
                <div className="form-group">
                  <label htmlFor="postLunch">POST LUNCH RESET</label>
                  <p className="time-description">
                    What time do you usually return to work after lunch?
                  </p>
                  <div className="input-wrapper custom-input-gradient-border">
                    <input
                      type="text"
                      id="postLunch"
                      className="input-field"
                      placeholder="Enter time"
                      value={scheduleData?.postLunch?.time}
                      onChange={(e) => handleTimeChange("postLunch", e)}
                      onBlur={(e) => handleTimeBlur("postLunch", e)}
                      aria-invalid={!!errors.postLunch}
                    />
                  </div>
                  {errors.postLunch && (
                    <div className="error-message">{errors.postLunch}</div>
                  )}
                </div>
              </div>

              <div className="schedule-time-input">
                <div className="form-group">
                  <label htmlFor="windDown">WIND DOWN</label>
                  <p className="time-description">
                    What time do you usually log off at work?
                  </p>
                  <div className="input-wrapper custom-input-gradient-border">
                    <input
                      type="text"
                      id="windDown"
                      className="input-field"
                      placeholder="Enter time"
                      value={scheduleData?.windDown?.time}
                      onChange={(e) => handleTimeChange("windDown", e)}
                      onBlur={(e) => handleTimeBlur("windDown", e)}
                      aria-invalid={!!errors.windDown}
                    />
                  </div>
                  {errors.windDown && (
                    <div className="error-message">{errors.windDown}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="submit"
                disabled={loading}
                className={`action-button ${
                  !loading &&
                  Object.keys(errors).length === 0 &&
                  scheduleData.startOfDay.time !== "" &&
                  scheduleData.postLunch.time !== "" &&
                  scheduleData.windDown.time !== ""
                    ? "action-button-active custom-input-gradient-border"
                    : "disable-submitBtn"
                } ${loading ? "p-25" : ""}`}
              >
                {loading ? (
                    <SpinnerLoader size={16} />
                ) : (
                  "Update"
                )}
              </button>
              <span className="cancelButton" onClick={handleOnClose}>
                Cancel
              </span>
            </div>
          </form>
        </div>
        </div>
      </div>
    </div>
  );
};

export default EditDailyScheduleModal;
